import { AntennaDoc } from "../schema/antenna-schema";
import { Antenna } from "./antenna";
import { DriveTestPoint } from "./drive-test";
import { PathTestPoint } from "./path-test"
import { PathWaypoint } from "./path-waypoint"
import { Point } from "./point"

export type MiscLeafNode = {
    latitude?: number;
    longitude?: number;
    altitudeEllipsoidMeters?: number;
    type: string;
    id: string|number;
    parentId: string|number;
    title: string;
    iconClass?: string;
    data: any; // cached correlation record, etc...
}

export interface MapPointInterface {
    latitude?: number;
    longitude?: number;
    altitudeEllipsoidMeters?: number;
    pointId?: string;
    type: string;

    id: string;
    parentId: string
    parentName?: string
    parentColor?: string
    parentHorizontalDistance?: number
    parentBearing?: number

    name?: string
    color ?: string
    exPointId?: string;
    position?: { lat: number, lng: number };

    disabled?: boolean;

    // tack on some misc children (like call log points)
    miscChildren?: MiscLeafNode[];
}

export abstract class MapPointBase<PointType>  {

    constructor( public pointData: PointType ) {}

    get pointId(): string { return this.pointData['pointId'] }
    get position() { return { lat: this.latitude, lng: this.longitude } }

    latitude: number
    longitude : number
    altitudeEllipsoidMeters: number
    parentId: string;
    parentName: string
    parentColor?: string
    parentHorizontalDistance: number
    parentBearing: number
    color?: string;
    miscChildren?: MiscLeafNode[];

    disabled?: boolean;
}

export class MapWaypoint extends MapPointBase<PathWaypoint> implements MapPointInterface {

    private _point?: Point;

    get pathWaypoint() { return this.pointData }
    get id() { return this.pointData?._id }
    get type() { return this.pointData?.type }
    get point() { return this._point }
    get name() {
        if ( this.type == 'HA' ) {
            return `HA ${Number(this.pointData.waypointOrder)?this.pointData.segment:''}`
        } else {
            return this.pointData.segment
        }
    }
    get exPointId() {
        switch ( this.type ) {
            case 'EX': return this.pointId
            default: return ''
        }
    }

    set point( point: Point ) {
        this._point = point;
        if ( point ) {
            this.latitude = Number(point.latitude)
            this.longitude = Number(point.longitude)
            this.altitudeEllipsoidMeters = Number(point.altitudeEllipsoidMeters)
        }
    }

    constructor(pathWaypoint: PathWaypoint, point: Point) {
        super(pathWaypoint)
        this.point = point
    }

}

// Just like a MapWaypoint except tack on a member for pathTestPoint
export class PathTestMapPoint extends MapWaypoint {

    constructor( pathWaypoint: PathWaypoint, point: Point, public pathTestPoint: PathTestPoint ) {
        super(pathWaypoint,point)
    }
}
export class DriveTestMapPoint extends MapWaypoint {

    constructor( pathWaypoint: PathWaypoint, point: Point, public driveTestPoint: DriveTestPoint ) {
        super(pathWaypoint,point)
    }
}

export class MapImageOverlayPoint {
    constructor(public x: number, public y: number) { }
}

export class MapImageOverlayDimension {
    constructor(public width: number, public height: number) { }
}

export class MapImageOverlayBounds {
    constructor(public topLeft: MapImageOverlayPoint, public bottomRight: MapImageOverlayPoint) { }
}

export class MapImageOverlay {

    imageId: string  // currently using scoutPhoto implementation to store image

    topLeft: MapImageOverlayPoint

    pixelSize: MapImageOverlayDimension

    imageSize: MapImageOverlayDimension

    bounds: MapImageOverlayBounds

    // Optional altitude metadata for 3D rendering contexts
    altitudeEllipsoidMeters?: number
    altitudeMeters?: number
    altitude?: number

    imageDataUrl?: string // not saved.  place to stash url if passing new overlay to plotter

    jpgName?: string

    jgwName?: string

    gpxName?: string

    routeId?:string
}

export class MapRouteGPXOverlay {
    id : string
    routeId: string
    gpxDataUrl?: string
    gpxName?: string
    updatedAt?: number
}

export class MapCellGridOverlay {
    // Grid anchored at top-left origin; dimensions in meters
    constructor(
        public originLat: number,
        public originLng: number,
        public cellWidthMeters: number,
        public cellHeightMeters: number,
        public rows: number,
        public cols: number,
        public id?: string,
        public name?: string,
    ) {}
}

// Device interface that matches antenna properties needed for mapping
export interface Device {
    id: string;
    device_type: string; // 'antenna', 'handset', etc.
    make?: string;
    model?: string;
    description?: string;
    latitude?: number;
    longitude?: number;
    height?: number; // altitude/height in meters
    azimuth?: number; // degrees clockwise from true north
    tilt?: number; // positive/negative tilt (degrees)
    range_meters?: number; // coverage radius
    azimuth_beam_width?: number; // horizontal beam width in degrees for coverage sector
    elevation_beam_width?: number; // vertical beam width in degrees (for future 3D scenarios)
    building_id?: string;
    floor_number?: number;
    floor_name?: string;
    // Optional style
    color?: string;
    iconClass?: string;
    // Icon configuration for map display
    iconRotationDegrees?: number; // degrees to rotate icon (0 = no rotation)
    iconAnchor?: 'center' | 'bottom-center'; // point around which icon rotates and is positioned
    iconOffsetX?: number; // horizontal pixel offset from anchor point
    iconOffsetY?: number; // vertical pixel offset from anchor point  
    shouldRotateIcon?: boolean; // whether icon should rotate based on azimuth
    // Optional: original source document for detailed display
    data?: any;
}

// Input type for MapDevice constructor
export interface MapDeviceInput extends Device {
    id: string;
}

// MapDevice class implements Device interface
export class MapDevice implements Device {
    public id: string;
    public device_type: string;
    public make?: string;
    public model?: string;
    public description?: string;
    public latitude?: number;
    public longitude?: number;
    public height?: number;
    public azimuth?: number;
    public tilt?: number;
    public range_meters?: number;
    public azimuth_beam_width?: number;
    public elevation_beam_width?: number;
    public building_id?: string;
    public floor_number?: number;
    public floor_name?: string;
    public color?: string;
    public iconClass?: string;
    public iconRotationDegrees?: number;
    public iconAnchor?: 'center' | 'bottom-center';
    public iconOffsetX?: number;
    public iconOffsetY?: number;
    public shouldRotateIcon?: boolean;
    public data?: any;

    constructor(input: MapDeviceInput) {
        Object.assign(this, input);
    }

    // Legacy properties for backward compatibility
    get category(): string { return this.device_type; }
    get radiusMeters(): number | undefined { return this.range_meters; }
    get azimuthDeg(): number | undefined { return this.azimuth; }
    get beamwidthDeg(): number | undefined { return this.azimuth_beam_width; }
    get altitudeMeters(): number | undefined { return this.height; }
    get name(): string | undefined { return this.id; }

    // Utility function to convert antenna to MapDevice
    static fromAntenna(antenna: Antenna|AntennaDoc): MapDevice {
        // Convert AntennaDoc to plain object if needed
        const antennaData = (antenna && 'toJSON' in antenna && typeof (antenna as any).toJSON === 'function') 
            ? (antenna as any).toJSON() 
            : antenna;
        // Set defaults for rypplzz antennas
        let azimuth_beam_width = antennaData.azimuth_beam_width ?? 360;
        let elevation_beam_width = antennaData.elevation_beam_width ?? 180;
        let range_meters = antennaData.range_meters;
        
        // Determine icon and configuration based on tilt value
        let iconClass = antennaData.iconClass;
        let iconRotationDegrees = 0;
        let iconAnchor: 'center' | 'bottom-center' = 'bottom-center';
        let iconOffsetX = 0;
        let iconOffsetY = 0;
        let shouldRotateIcon = true;
        
        if (!iconClass) {
            const mountType = antennaData.mount_type;

            // Determine icon based on mount type
            if (mountType === 'ceiling') {
                // Ceiling-mounted antennas point downward
                iconClass = 'far fa-life-ring';
                iconAnchor = 'center';
                shouldRotateIcon = false;
            } else if (mountType === 'floor' || mountType === 'desktop') {
                // Floor/desktop-mounted antennas point upward
                iconClass = 'fas fa-life-ring';
                iconAnchor = 'center';
                shouldRotateIcon = false;
            } else {
                // All other mount types (wall, tower, pole, roof, mast, monopole, lattice, panel, rack, satellite, outdoor, indoor, or null)
                // Default to directional WiFi icon
                iconClass = 'fas fa-wifi';
                iconAnchor = 'bottom-center';
                shouldRotateIcon = true;
                // WiFi icon points up (North) by default. Azimuth uses East=0°, CCW positive.
                // CSS rotate() is clockwise-positive, so use (90 - azimuth) to align visually.
                iconRotationDegrees = 90 - (antennaData.azimuth || 0);
                iconOffsetY = 2;
                iconOffsetX = 2;
            }
        }
        
        return new MapDevice({
            id: antennaData.antenna_id || antennaData.id, // Use antenna_id if available, fallback to id for backward compatibility
            device_type: 'antenna',
            make: antennaData.make,
            model: antennaData.model,
            description: antennaData.description,
            latitude: antennaData.latitude,
            longitude: antennaData.longitude,
            height: antennaData.height,
            azimuth: antennaData.azimuth,
            tilt: antennaData.tilt,
            range_meters,
            azimuth_beam_width,
            elevation_beam_width,
            building_id: antennaData.building_id,
            floor_number: antennaData.floor_number,
            floor_name: antennaData.floor_name,
            color: antennaData.color || 'rgba(28, 126, 214)', // default antenna color with transparency
            iconClass,
            iconRotationDegrees,
            iconAnchor,
            iconOffsetX,
            iconOffsetY,
            shouldRotateIcon,
            data: antennaData // so map popup can show full details of antenna
        });
    }
}


export class MapConfigBaseLayer {

    static readonly OpenStreetLayer : MapConfigBaseLayer = {
        title: 'OpenStreet',
        mapUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        maxZoom: 24,
        maxNativeZoom: 18,
        useCache: true,
        crossOrigin: true,
        zIndex: 15
    }

    static readonly OsmbgLayer : MapConfigBaseLayer = {
        title: "osmbg",
        mapUrl: "/maptiles/osmbg/{z}/{x}/{y}.png",
        maxZoom: 24,
        maxNativeZoom: 21,
        useCache: false,
        crossOrigin: true,
        zIndex: 20
    }

    constructor(
        public title: string,
        public mapUrl: string,
        public maxZoom = 24,
        public maxNativeZoom = 18,
        public useCache = true,
        public crossOrigin = true,
        public zIndex = 20 ) {
    }

}
